import axios from "axios";

/**
 * True when a string body looks like an XML or HTML document rather than a
 * plain-text message. Upstreams we don't control — API gateways, load
 * balancers, S3/MinIO, Play's HTML dev error page — return markup error bodies,
 * and dumping that verbatim into a user-facing alert is worse than a generic
 * message. A trimmed body that opens with `<` covers `<?xml`, `<!DOCTYPE`,
 * `<html>`, and S3-style `<Error>` envelopes; a real plain-text error never
 * starts that way.
 */
function looksLikeMarkup(value: string): boolean {
  return value.startsWith("<");
}

/**
 * The message the *server* put in a response body, or `null` when it sent none
 * worth showing. Backends return error detail in three shapes, handled in
 * order: a JSON object `{ error }` / `{ message }`; a bare plain-text body
 * (several REST endpoints reply with plain text, e.g. the subscription restart
 * / start-trial flows); otherwise nothing. An XML/HTML body from an upstream we
 * don't control (API gateway, load balancer, S3/MinIO, Play's HTML error page)
 * is treated as "none" so it isn't shown raw.
 *
 * This is the single definition of "what the server said", so a caller that
 * needs to distinguish a real server explanation from a fallback (e.g. to pick
 * a domain-specific default) can ask directly rather than pattern-matching the
 * transport text out of a message string.
 */
export function getServerErrorMessage(data: unknown): string | null {
  if (data && typeof data === "object") {
    const record = data as { error?: unknown; message?: unknown };
    if (typeof record.error === "string" && record.error.trim().length > 0) {
      return record.error;
    }
    if (typeof record.message === "string" && record.message.trim().length > 0) {
      return record.message;
    }
  }

  if (typeof data === "string") {
    const trimmed = data.trim();
    if (trimmed.length > 0 && !looksLikeMarkup(trimmed)) {
      return trimmed;
    }
  }

  return null;
}

/**
 * Pull a human-readable message out of any thrown value, preferring what the
 * server sent. For an axios error the message can come from two very different
 * places, and the distinction is the whole point (issue #2931):
 *   - `error.response.data` is what the *server* said. It always wins.
 *   - `error.message` is what *axios* said about its own failure ("Network
 *     Error", "Request failed with status code 500", "timeout exceeded"). It is
 *     never server content, so it must not outrank the caller's `fallbackMessage`
 *     — a caller that wrote "An error occurred during signup" means it to be
 *     shown, not overwritten with "Network Error".
 *
 * Server detail (via {@link getServerErrorMessage}) always wins. With none, the
 * caller's `fallbackMessage` is used — and callers via {@link toApiError} always
 * get a non-empty one (its default), so a user is shown that default rather than
 * axios's transport text. The `|| error.message` tail is only a guard for a
 * caller that deliberately passes an empty fallback; the transport string is
 * otherwise never surfaced (it stays on `originalError` for logs).
 */
export function getApiErrorMessage(error: unknown, fallbackMessage: string): string {
  if (axios.isAxiosError(error)) {
    const serverMessage = getServerErrorMessage(error.response?.data);
    if (serverMessage !== null) {
      return serverMessage;
    }

    // `error.message` is axios describing its own transport failure, not content
    // the server sent, so the caller's fallback outranks it (issue #2931).
    return fallbackMessage || error.message;
  }

  if (error instanceof Error) {
    return error.message || fallbackMessage;
  }

  return fallbackMessage;
}
